"use client";

import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";

type PaymentEvent = {
  id: number;
  severity: "info" | "warning" | "error";
  event_type: string;
  message: string;
  finix_transfer_id: string | null;
  pending_charge_id: number | null;
  agreement_address: string | null;
  created_at: string;
  resolved_at: string | null;
};

const SEVERITY_CLASS: Record<PaymentEvent["severity"], string> = {
  error: "bg-red-500/20 text-red-300",
  warning: "bg-amber-500/20 text-amber-300",
  info: "bg-blue-500/20 text-blue-300",
};

export const dynamic = "force-dynamic";

export default function AdminPaymentEventsPage() {
  const { ready, authenticated, login, getAccessToken } = usePrivy();
  const [events, setEvents] = useState<PaymentEvent[] | null>(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const token = await getAccessToken();
      if (!token) throw new Error("Sign in again to view payment events.");
      const response = await fetch(`${API_BASE_URL}/api/admin/payment-events?open=${openOnly}`, {
        cache: "no-store",
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.error || "Unable to load payment events.");
      setEvents(payload.events);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load payment events.");
    }
  }, [getAccessToken, openOnly]);

  useEffect(() => {
    if (ready && authenticated) void load();
  }, [ready, authenticated, load]);

  async function resolve(id: number) {
    const token = await getAccessToken();
    if (!token) return;
    await fetch(`${API_BASE_URL}/api/admin/payment-events/${id}/resolve`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    await load();
  }

  if (!ready || !authenticated) {
    return (
      <main className="min-h-screen bg-slate-950 px-4 py-12 text-white">
        <button type="button" onClick={login} className="rounded-xl bg-blue-600 px-5 py-3 font-semibold">
          Sign in to view payment events
        </button>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-10 text-white md:px-8">
      <div className="mx-auto max-w-7xl">
        <Link href="/admin/dashboard" className="text-sm text-blue-300">
          Back to dashboard
        </Link>
        <h1 className="mt-3 text-4xl font-bold">Payment Events</h1>
        <p className="mt-2 text-slate-300">
          Failed deposits, refunds, disputes, rejected webhooks and unmatched settlements. Resolve an item once it has been handled.
        </p>

        <label className="mt-6 flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={openOnly} onChange={(event) => setOpenOnly(event.target.checked)} />
          Show unresolved only
        </label>

        {error && <p className="mt-4 rounded-xl bg-red-500/10 p-4 text-red-300">{error}</p>}
        {events && events.length === 0 && <p className="mt-6 text-slate-400">No events.</p>}

        {events && events.length > 0 && (
          <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900 text-slate-400">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Severity</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Details</th>
                  <th className="px-4 py-3">Finix transfer</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className="border-t border-slate-800 align-top">
                    <td className="px-4 py-3 whitespace-nowrap">{new Date(event.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${SEVERITY_CLASS[event.severity]}`}>
                        {event.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{event.event_type}</td>
                    <td className="px-4 py-3">{event.message}</td>
                    <td className="px-4 py-3 font-mono text-xs">{event.finix_transfer_id ?? "-"}</td>
                    <td className="px-4 py-3">
                      {event.resolved_at ? (
                        <span className="text-slate-500">Resolved</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void resolve(event.id)}
                          className="rounded-lg border border-slate-600 px-3 py-1 text-xs hover:bg-slate-800"
                        >
                          Mark resolved
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
